import { motion } from 'framer-motion';
import styled from 'styled-components';

import { FadeVariant, fadeVariants } from '@pkg/motion';
import { LayoutPosition, styledLayout, styledReset } from '@pkg/primitives';

import { backdropTransition, InteriorVariant, interiorVariants } from './Sheet.motion';
import { type SheetSize } from './Sheet.types';
import { tokens } from './tokens';

interface StyledSheetProps {
    $size?: SheetSize;
}

interface InteriorProps {
    $gapless?: boolean;
}

export const Backdrop = styled(motion.div).attrs({
    variants: fadeVariants,
    initial: FadeVariant.Hidden,
    animate: FadeVariant.Show,
    exit: FadeVariant.Hidden,
    transition: backdropTransition,
})`
    ${styledReset.borderBox};
    ${styledLayout.positionCover({ position: LayoutPosition.Fixed })};
    z-index: ${tokens.zindex.temp};
    background-color: ${({ theme }) => theme.shades.black['30']};
`;

export const StyledSheet = styled(motion.div)<StyledSheetProps>`
    ${styledReset.borderBox};
    ${styledLayout.positionCover({ position: LayoutPosition.Fixed })};

    --sheet-width: 100%;

    z-index: ${tokens.zindex.temp};
    width: var(--sheet-width);
    max-width: 100%;
    // NOTE: For as long as this component is full height
    // on desktop, then the "tuck" prop does not apply.
    height: 100%;
    max-height: 100vh;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        --sheet-width: ${({ $size = 'medium' }) => `${tokens.sheet.width[$size]}px`};

        left: auto;
        padding: ${tokens.spacing.tight}px;
    }
`;

export const Card = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    // TODO: This background style should be moved into a mixin.
    background: linear-gradient(
        100deg,
        ${({ theme }) => theme.colors.purpleDark} 40%,
        ${({ theme }) => theme.colors.blueDark}
    );
    background-repeat: no-repeat;
    background-position: 100% 100%;
    background-size: cover;
    background-attachment: fixed;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        box-shadow: -16px 0 80px 16px ${({ theme }) => theme.shades.black[55]};
        border-radius: ${tokens.card.radius}px;
    }
`;

export const Interior = styled(motion.div).attrs({
    variants: interiorVariants,
    initial: InteriorVariant.Hidden,
    animate: InteriorVariant.Show,
    exit: InteriorVariant.Hidden,
})<InteriorProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        gap: ${({ $gapless }) => ($gapless ? undefined : `${tokens.spacing.tight}px`)};
    }
`;
